// src/features/atividade/arteGps.js
//
// Os DADOS da arte de atividade GPS no carrossel de compartilhar — o mesmo
// carrossel do pós-check-in e da refeição (`ShareRefeicao`). Esta é a parte
// pura: métricas já formatadas, o traçado desenhado num SVG e o recorte de
// privacidade das pontas. Quem desenha o cartão é o `ShareCard` (variantes
// `1t` e `1u`), como todas as outras artes.
//
// Desenho: "Compartilhar Atividade (1).html" (Claude Design, turno 1) — métricas
// empilhadas e centralizadas, traçado amarelo embaixo, LesVita no rodapé. Um
// layout em quatro fundos: sticker transparente (1a), escuro (1b), marca (1c)
// e foto do usuário (1d). Aqui o 1a, o 1b e o 1d são a MESMA arte (`1t`), que
// troca de fundo com o que a tela já tem (o interruptor "Sem fundo" e a foto
// do registro); o 1c é a `1u`.
//
// ─── NADA SAI DAQUI PARA UM SERVIÇO EXTERNO ─────────────────────────────────
//
// Mesma regra do card antigo (`compartilharAtividade.js`): o traçado é
// desenhado no aparelho, sem tile e sem serviço de imagem. O percurso de
// alguém é casa, academia, escola do filho.
//
// ─── POR QUE AS PONTAS SÃO CORTADAS POR PADRÃO ──────────────────────────────
//
// Onde a corrida começa e termina é, quase sempre, onde a pessoa mora. O
// desenho corta 200 m de cada ponta (o "Ocultar início e fim" do desenho,
// ligado por padrão); quem quiser o percurso inteiro desliga na tela.
//
// ─── POR QUE O TRAÇADO É UMA <img> DE PNG, E NÃO UM <svg> NO CARTÃO ─────────
//
// O html2canvas serializa um <svg> inline e o pinta no tamanho de LAYOUT — o
// cartão tem 270 px e o arquivo sai 4× maior, então o traço sairia borrado. Uma
// <img> é desenhada a partir do tamanho NATURAL da imagem: com o bitmap em 4×
// (720×600 para a caixa de 180×150), o arquivo de 1080×1920 sai nítido. E a
// sombra do sticker vai DENTRO da imagem, porque o html2canvas ignora
// `filter: drop-shadow` do CSS. Era um SVG em data URL até o arquivo baixado no
// iPhone sair sem o traçado — ver `imagemDoTracado`.

import { haversine, velocidadeKmh } from './geo';
import { decodificar, limites, simplificar } from './polyline';
import { CORES_TRACADO } from './mapa/estiloMapa';
import { modalidade as obterModalidade } from './atividadeConfig';
import { elevacaoDaAtividade } from './estatisticas';
import { montarAtividadeDoCheckin } from './atividadeContrato';
import { formatarNumero } from '../../i18n/formatos';

/** Quanto de cada ponta some do desenho com o "Ocultar início e fim". */
export const METROS_OCULTOS = 200;

/** Caixa do traçado, em unidades do viewBox (a mesma do desenho). */
const CAIXA = { w: 180, h: 150 };
/** Tamanho natural do SVG: 4× a caixa, a escala da exportação do story. */
const ESCALA_NATURAL = 4;
/** Traço de 7 unidades — o `stroke-width` do desenho. */
const TRACO = 7;
/** Folga da borda: meio traço + o borrão da sombra. */
const MARGEM = 14;
/** Teto de vértices: o traçado ocupa ~400 px no PNG; mais que isso é peso. */
const MAX_VERTICES = 600;

const RAD = Math.PI / 180;
const mercatorY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * RAD) / 2));

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// ── Formatos do desenho ──────────────────────────────────────────────────────
//
// O desenho escreve "5:14 /km" e "31min 28s" — não o 5'14" e o 31:28 da tela
// de detalhe. Num story, quem lê não é quem correu: "31min 28s" não precisa de
// legenda para ser entendido.

/** `5:14 /km` */
export function formatarRitmo(segundosPorKm) {
  const v = Math.round(num(segundosPorKm));
  if (v <= 0) return '--';
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')} /km`;
}

/** `31min 28s` · `1h 04min` · `45s` */
export function formatarDuracao(segundos) {
  const total = Math.max(0, Math.floor(num(segundos)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  if (m > 0) return `${m}min ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

// ── Privacidade ──────────────────────────────────────────────────────────────

/**
 * Corta `metros` de cada ponta do traçado.
 *
 * Percurso curto (menos que três vezes o corte somado) corta proporcional —
 * 30% de cada lado —, senão uma volta de 500 m viraria um risco de 100 m e uma
 * de 300 m sumiria. O meio sempre sobra.
 *
 * @param {Array<{lat:number,lng:number}>} pontos
 * @param {number} metros
 * @returns {Array<{lat:number,lng:number}>}
 */
export function aparaPontas(pontos, metros = METROS_OCULTOS) {
  if (!Array.isArray(pontos) || pontos.length < 3 || !(metros > 0)) return pontos || [];

  const acumulado = [0];
  for (let i = 1; i < pontos.length; i += 1) {
    acumulado.push(acumulado[i - 1] + haversine(pontos[i - 1], pontos[i]));
  }
  const total = acumulado[acumulado.length - 1];
  if (!(total > 0)) return pontos;

  const corte = total >= metros * 2 * 3 ? metros : total * 0.3;

  let inicio = 0;
  while (inicio < pontos.length - 1 && acumulado[inicio] < corte) inicio += 1;
  let fim = pontos.length - 1;
  while (fim > 0 && total - acumulado[fim] < corte) fim -= 1;

  if (fim - inicio < 1) {
    // Nada sobrou entre os dois cortes (pontos muito esparsos): fica o par do
    // meio, que não revela ponta nenhuma.
    const meio = Math.floor(pontos.length / 2);
    return pontos.slice(Math.max(0, meio - 1), meio + 1);
  }
  return pontos.slice(inicio, fim + 1);
}

// ── Desenho do traçado ───────────────────────────────────────────────────────

/**
 * O `d` do path do traçado dentro da caixa 180×150, norte em cima.
 *
 * Mercator com escala ÚNICA nos dois eixos (a ideia do `TracadoSvg`): uma reta
 * de 5 km continua reta, e não esticada até a diagonal da caixa.
 *
 * @param {Array<{lat:number,lng:number}>} pontos
 * @returns {string|null}
 */
export function caminhoDoTracado(pontos) {
  if (!Array.isArray(pontos) || pontos.length < 2) return null;

  let usados = pontos;
  if (usados.length > MAX_VERTICES) {
    let tolerancia = 1e-5;
    for (let i = 0; i < 7 && usados.length > MAX_VERTICES; i += 1) {
      usados = simplificar(pontos, tolerancia);
      tolerancia *= 3;
    }
  }

  const b = limites(usados);
  if (!b) return null;

  // Os DOIS eixos em radianos: `mercatorY` devolve radianos, então a
  // longitude também tem de entrar em radianos — misturar com graus achata o
  // percurso ~57× na vertical. E o norte tem o MAIOR y de Mercator: a altura
  // é norte − sul, nunca o contrário (negativa, viraria o piso de 1e-9).
  const x0 = b.minLng * RAD;
  const yNorte = mercatorY(b.maxLat);
  const larguraGeo = Math.max(1e-9, (b.maxLng - b.minLng) * RAD);
  const alturaGeo = Math.max(1e-9, yNorte - mercatorY(b.minLat));

  const escala = Math.min(
    (CAIXA.w - MARGEM * 2) / larguraGeo,
    (CAIXA.h - MARGEM * 2) / alturaGeo
  );
  const dx = (CAIXA.w - larguraGeo * escala) / 2;
  const dy = (CAIXA.h - alturaGeo * escala) / 2;

  // mercatorY cresce para o norte; no SVG o y cresce para baixo. Medir a
  // partir do y do NORTE põe o norte em cima.
  return usados
    .map((p, i) => {
      const x = (num(p.lng) * RAD - x0) * escala + dx;
      const y = (yNorte - mercatorY(num(p.lat))) * escala + dy;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

/**
 * O traçado como data URL de PNG, pronto para uma `<img>`.
 *
 * PNG, e não SVG, por causa do arquivo baixado no aparelho (relato de 12/10):
 * a prévia mostrava o traçado — quem desenha ali é o navegador — e o PNG saía
 * só com as métricas. O html2canvas manda `data:image/svg+xml` por um caminho
 * só dele (`isSVG` → `SUPPORT_SVG_DRAWING` → `drawImage` de um SVG recém-
 * carregado), e é esse caminho que falha na WebView; no Chromium e no WebKit
 * de desktop ele funciona, por isso não se reproduz fora do aparelho. Uma
 * imagem raster passa pelo mesmo caminho da foto do registro, que sempre saiu
 * certa. `Path2D` lê o `d` do SVG tal como está, então o desenho é o mesmo.
 *
 * Fora do navegador (teste em Node, jsdom sem canvas) cai no SVG, que é o que
 * havia antes.
 *
 * @param {string} d path de `caminhoDoTracado`
 * @param {{sombra?: boolean}} opcoes `sombra`: a do sticker e da foto
 *   (`drop-shadow(0 2px 6px rgba(0,0,0,.5))` do desenho, na escala do viewBox)
 * @returns {string}
 */
export function imagemDoTracado(d, { sombra = false } = {}) {
  return pngDoTracado(d, { sombra }) || svgDoTracado(d, { sombra });
}

function pngDoTracado(d, { sombra }) {
  if (typeof document === 'undefined' || typeof Path2D === 'undefined') return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = CAIXA.w * ESCALA_NATURAL;
    canvas.height = CAIXA.h * ESCALA_NATURAL;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.scale(ESCALA_NATURAL, ESCALA_NATURAL);
    if (sombra) {
      // A sombra do canvas NÃO segue o `scale`: deslocamento e borrão são em
      // pixels do bitmap. Daí o × ESCALA_NATURAL — e o borrão do canvas é ~2σ
      // do `stdDeviation` do feDropShadow.
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 3 * ESCALA_NATURAL;
      ctx.shadowBlur = 2 * 4.5 * ESCALA_NATURAL;
    }
    ctx.strokeStyle = CORES_TRACADO.linha;
    ctx.lineWidth = TRACO;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke(new Path2D(d));
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function svgDoTracado(d, { sombra }) {
  const largura = CAIXA.w * ESCALA_NATURAL;
  const altura = CAIXA.h * ESCALA_NATURAL;
  // A região do filtro em unidades do USUÁRIO: em objectBoundingBox, um
  // percurso reto na horizontal tem caixa de altura zero e o filtro apagaria
  // o traço inteiro.
  const filtro = sombra
    ? `<defs><filter id="s" filterUnits="userSpaceOnUse" x="-10" y="-10" width="${CAIXA.w + 20}" height="${CAIXA.h + 20}"><feDropShadow dx="0" dy="3" stdDeviation="4.5" flood-color="#000" flood-opacity="0.5"/></filter></defs>`
    : '';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${largura}" height="${altura}" viewBox="0 0 ${CAIXA.w} ${CAIXA.h}">` +
    filtro +
    `<path d="${d}" fill="none" stroke="${CORES_TRACADO.linha}" stroke-width="${TRACO}" stroke-linecap="round" stroke-linejoin="round"${sombra ? ' filter="url(#s)"' : ''}/>` +
    '</svg>';
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// ── Métricas ─────────────────────────────────────────────────────────────────

/** As métricas EXTRAS que a tela oferece como chip (no máximo uma por vez). */
export const EXTRAS_GPS = [
  { chave: 'elevacao', rotulo: 'Elevação' },
  { chave: 'calorias', rotulo: 'Calorias' },
  { chave: 'passos', rotulo: 'Passos' },
];

/**
 * Tudo que as artes `1t`/`1u` precisam, ou `null` quando a atividade não tem
 * traçado (aí a tela fica exatamente como era: só as artes de treino).
 *
 * @param {object} atividade documento de ATIVIDADES
 * @param {{ocultarPontas?: boolean, extra?: string|null}} opcoes
 * @returns {object|null}
 */
export function montarDadosGps(atividade, { ocultarPontas = true, extra = null } = {}) {
  if (!atividade) return null;
  const brutos = decodificar(atividade.POLYLINE || '');
  if (brutos.length < 2) return null;

  const pontos = ocultarPontas ? aparaPontas(brutos) : brutos;
  const d = caminhoDoTracado(pontos);
  if (!d) return null;

  const mod = obterModalidade(atividade.TIPO);
  const km = num(atividade.DISTANCIA_M) / 1000;
  const usaPace = mod?.usaPace !== false;

  const metricas = [
    {
      chave: 'distancia',
      rotulo: 'Distância',
      valor: `${formatarNumero(km, { min: 2, max: 2 })} km`,
    },
    usaPace
      ? { chave: 'ritmo', rotulo: 'Ritmo', valor: formatarRitmo(atividade.PACE_MEDIO_S) }
      : {
          chave: 'velocidade',
          rotulo: 'Velocidade',
          valor: `${formatarNumero(velocidadeKmh(atividade.DISTANCIA_M, atividade.DURACAO_S), {
            min: 1,
            max: 1,
          })} km/h`,
        },
    { chave: 'tempo', rotulo: 'Tempo', valor: formatarDuracao(atividade.DURACAO_S) },
  ];

  // Extras: só os que a atividade TEM. Um chip de "0 passos" na bike seria a
  // tela oferecendo um número que não existe.
  const elevacao = elevacaoDaAtividade({
    elevacaoPos: atividade.ELEVACAO_POS,
    elevacaoNeg: atividade.ELEVACAO_NEG,
    amostras: Array.isArray(atividade.SAMPLES) ? atividade.SAMPLES : [],
  });
  const valoresExtras = {
    elevacao: elevacao.pos > 0 ? `${formatarNumero(elevacao.pos)} m` : null,
    calorias:
      num(atividade.CALORIAS) > 0
        ? `${formatarNumero(Math.round(num(atividade.CALORIAS)))} kcal`
        : null,
    passos:
      mod?.usaPassos !== false && num(atividade.PASSOS) > 0
        ? formatarNumero(Math.round(num(atividade.PASSOS)))
        : null,
  };
  const extrasDisponiveis = EXTRAS_GPS.filter((e) => valoresExtras[e.chave]);
  const escolhido = extrasDisponiveis.find((e) => e.chave === extra);
  if (escolhido) {
    metricas.push({
      chave: escolhido.chave,
      rotulo: escolhido.rotulo,
      valor: valoresExtras[escolhido.chave],
    });
  }

  return {
    metricas,
    extrasDisponiveis,
    tracado: {
      comSombra: imagemDoTracado(d, { sombra: true }),
      semSombra: imagemDoTracado(d, { sombra: false }),
    },
  };
}

/**
 * O "treino" da atividade quando o registro de treino dela ainda não pode ser
 * lido (fila de envio, sem rede). É o formato do check-in que as outras artes
 * leem — a mesma montagem que o salvamento usa (`montarAtividadeDoCheckin`) —,
 * com as calorias e o título do documento.
 *
 * @param {object} atividade documento de ATIVIDADES
 * @returns {object}
 */
export function treinoDaAtividade(atividade = {}) {
  const mod = obterModalidade(atividade.TIPO);
  const [item] = montarAtividadeDoCheckin({
    modalidadeId: atividade.TIPO,
    resumoAtividade: {
      duracaoS: num(atividade.DURACAO_S),
      distanciaM: num(atividade.DISTANCIA_M),
      passos: num(atividade.PASSOS),
      paceMedioS: num(atividade.PACE_MEDIO_S),
      elevacaoPos: num(atividade.ELEVACAO_POS),
    },
    pesoKg: null,
  });
  if (num(atividade.CALORIAS) > 0) item.calories = Math.round(num(atividade.CALORIAS));
  return {
    id: atividade.CHECKIN_ID || atividade.id || '',
    title: String(atividade.TITULO || mod?.nome || 'Atividade'),
    description: String(atividade.DESCRICAO || ''),
    photo_url: '',
    atividades: [item],
    atividade_gps_id: atividade.id || null,
  };
}

export default {
  aparaPontas,
  caminhoDoTracado,
  imagemDoTracado,
  montarDadosGps,
  treinoDaAtividade,
  formatarRitmo,
  formatarDuracao,
};
